/**
 * /start: upload first, account after (2026-10-16).
 *
 * Last week 15 people reached /signup and 5 got in, and 8 of the 10 who
 * left never typed anything. Everyone who did get in and uploaded saw a
 * first look. So this page asks for the bill first, before any account,
 * and only then asks where to send the first look (email and a code).
 *
 * Nothing is read until they've confirmed their email. The upload sits in
 * pending_uploads until then, and AppGate claims it after they log in.
 * Uploads nobody claims are kept for now, see PENDING_UPLOAD_RETENTION_DAYS
 * on the server.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, Check, FileText, Lock, Plus, Upload, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { API_BASE } from '../lib/apiConfig';
import { shrinkImage } from '../lib/shrinkImage';
import { prepareImageForUpload } from '../lib/imageUpload';
import { notePendingEmail, requestBillReminder, setPendingPhone, signInWithReminderToken, uploadWithoutAccount } from '../lib/freeStart';
import { toE164 } from '../lib/phone';
import { aliasLandingId, phFailure, phOauthStarted, phSignupOtpSent, phStartStep } from '../lib/posthog';
import { markSignInStarted } from '../lib/lastSignIn';
import { captureAttributionFromUrl } from '../lib/attribution';
import AuthHelp from '../components/AuthHelp';
import googleLogo from '../assets/google.svg';
import '@styles/pages/signup.css';
import '@styles/pages/start.css';

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*';
const OK_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
const MAX_FILES = 6;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// start_viewed fired twice per visit (the /signup redirect lands here and
// the effect can run again). Once per page load is what the funnel wants.
let startViewedSent = false;

function okFile(f: File): boolean {
  const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
  return OK_EXT.includes(ext) || f.type.startsWith('image/') || f.type === 'application/pdf';
}

export default function Start() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'upload' | 'email'>('upload');
  const [files, setFiles] = useState<File[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  // Required for both ways in (2026-11-02), so every lead can be called.
  // It sits above the Google button because Google doesn't give us one.
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  // Phones and tablets get "Take a photo". On a computer that button would
  // just open the file picker, so it's one upload button there instead.
  const [hasCamera] = useState(() => {
    try {
      return window.matchMedia('(pointer: coarse)').matches;
    } catch {
      return false;
    }
  });
  const [dragging, setDragging] = useState(false);
  // "No bill with you?" (2026-10-26): an email now, the upload later.
  const [remindOpen, setRemindOpen] = useState(false);
  const [remindEmail, setRemindEmail] = useState('');
  const [remindProvider, setRemindProvider] = useState('');
  const [remindDue, setRemindDue] = useState('');
  const [remindDone, setRemindDone] = useState(false);
  // True while a link from a reminder email is signing them in.
  const [linking, setLinking] = useState(() => new URLSearchParams(window.location.search).has('rt'));
  const pickRef = useRef<HTMLInputElement>(null);

  // Already logged in: the normal upload form is the right place.
  useEffect(() => {
    captureAttributionFromUrl();
    // Ties this visit to the marketing site visit, same as /signup does.
    const landingId = new URLSearchParams(window.location.search).get('ph_distinct_id');
    if (landingId) aliasLandingId(landingId);
    // The plan button they clicked on the site, for the pay popup later.
    const want = new URLSearchParams(window.location.search).get('want');
    if (want === 'one_off' || want === 'annual') {
      try {
        localStorage.setItem('wanted_plan', want);
      } catch {
        /* the popup just defaults to Pay Per Bill */
      }
    }
    void (async () => {
      // From a reminder email: the link proves the address, so sign them
      // in and go straight to the upload. If the link is used or expired
      // they just get the normal page below.
      const url = new URL(window.location.href);
      const rt = url.searchParams.get('rt');
      if (rt) {
        url.searchParams.delete('rt');
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        const ok = await signInWithReminderToken(rt);
        phStartStep('reminder_link_opened', { signed_in: ok });
        setLinking(false);
      }
      const { data } = await supabase.auth.getSession();
      if (data.session?.user) navigate('/add-bill', { replace: true });
      else if (!startViewedSent) {
        startViewedSent = true;
        phStartStep('viewed', { device: hasCamera ? 'phone' : 'computer' });
      }
    })();
  }, [navigate, hasCamera]);

  const askForReminder = async () => {
    const normalized = remindEmail.trim().toLowerCase();
    if (busy) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(normalized)) {
      setError('Enter a valid email address.');
      return;
    }
    setBusy(true);
    setError(null);
    const problem = await requestBillReminder({ email: normalized, provider: remindProvider.trim(), dueDate: remindDue });
    setBusy(false);
    if (problem) {
      phFailure({ step: 'start_remind', message: problem });
      setError(problem);
      return;
    }
    // Never the hospital name here, it says they had care there.
    phStartStep('remind_requested', { has_hospital: Boolean(remindProvider.trim()), has_due_date: Boolean(remindDue) });
    setRemindDone(true);
  };

  const addFiles = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setError(null);
    const picked = Array.from(list).filter(okFile);
    if (picked.length < list.length) {
      const bad = Array.from(list).find((f) => !okFile(f));
      phFailure({
        step: 'start_file_rejected',
        message: 'unsupported_type',
        context: { file_type: bad?.type || bad?.name.split('.').pop() || null },
      });
      setError('That file type won\'t work. Use a PDF or a photo.');
    }
    const ready: File[] = [];
    for (const f of picked) {
      const prepared = await prepareImageForUpload(f);
      const shrunk = prepared.type.startsWith('image/') ? await shrinkImage(prepared) : prepared;
      if (shrunk.size > MAX_FILE_SIZE) {
        phFailure({
          step: 'start_file_rejected',
          message: 'too_large',
          context: { file_type: f.type || null, size_mb: Math.round(shrunk.size / 1e5) / 10 },
        });
        setError(`${f.name} is over 10 MB. Try a photo instead, or a smaller PDF.`);
        continue;
      }
      ready.push(shrunk);
    }
    setFiles((now) => {
      const next = [...now, ...ready].slice(0, MAX_FILES);
      if (ready.length) phStartStep('files_picked', { files: next.length });
      return next;
    });
  };

  const upload = async () => {
    if (!files.length || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { id, uploaded } = await uploadWithoutAccount(files, { dueDate: dueDate || null });
      setPendingId(id);
      phStartStep('uploaded', { files: uploaded, has_due_date: Boolean(dueDate) });
      setStep('email');
      window.scrollTo({ top: 0 });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Try again.';
      phFailure({ step: 'start_upload', message });
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  /** Checks the phone field. Saves it for the claim and returns it, or shows what's wrong. */
  const takePhone = (): string | null => {
    const e164 = toE164(phone);
    if (!e164) {
      setPhoneError(phone.trim() ? 'Enter a US phone number, like (555) 123-4567.' : 'Add your phone number.');
      return null;
    }
    setPhoneError(null);
    setPendingPhone(e164);
    return e164;
  };

  const sendCode = async () => {
    const normalized = email.trim().toLowerCase();
    if (busy) return;
    const e164 = takePhone();
    if (!e164) return;
    // The button is never greyed out (people tapped the grey one on /signup
    // and left). Say what's missing instead.
    if (!firstName.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(normalized)) {
      setError(!firstName.trim() ? 'Add your first name.' : 'Enter a valid email address.');
      return;
    }
    setBusy(true);
    setError(null);
    if (pendingId) notePendingEmail(pendingId, normalized);

    // Same two steps as /signup: our server makes the account (public
    // signups are off in Supabase), then Supabase sends the code.
    try {
      const claim = await fetch(`${API_BASE}/auth/claim-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalized, first_name: firstName.trim(), phone: e164 }),
      });
      if (!claim.ok) {
        const body = (await claim.json().catch(() => ({}))) as { error?: string };
        phFailure({ step: 'start_claim', message: body.error ?? `http_${claim.status}`, code: claim.status });
        setError(body.error ?? 'We could not create your account. Try again in a moment.');
        setBusy(false);
        return;
      }
    } catch {
      phFailure({ step: 'start_claim', message: 'network_error' });
      setError("We couldn't reach the server. Check your connection and try again.");
      setBusy(false);
      return;
    }

    const { error: otpError } = await supabase.auth.signInWithOtp({
      email: normalized,
      options: {
        emailRedirectTo: `${window.location.origin}/verify?email=${encodeURIComponent(normalized)}`,
        shouldCreateUser: false,
      },
    });
    setBusy(false);
    if (otpError) {
      phFailure({ step: 'start_otp_send', message: otpError.message });
      setError(otpError.message);
      return;
    }

    markSignInStarted('email');
    phSignupOtpSent(normalized);
    phStartStep('email_submitted', { method: 'email' });
    navigate(`/verify?email=${encodeURIComponent(normalized)}&intent=signup`);
  };

  const google = async () => {
    setError(null);
    if (!takePhone()) return;
    phOauthStarted({ provider: 'google', intent: 'signup' });
    phStartStep('email_submitted', { method: 'google' });
    markSignInStarted('google');
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/dashboard?from=signup` },
    });
    if (oauthError) {
      phFailure({ step: 'start_oauth_google', message: oauthError.message });
      setError(oauthError.message);
    }
  };

  return (
    <div className="auth-container">
      <header className="auth-header">
        <a href="https://mediloop.ai" className="logo" aria-label="Go to the mediloop homepage">
          <img src="/images/logos/tail-logo.png" alt="mediloop" className="logo-img" width={36} height={36} />
        </a>
      </header>

      {linking ? (
        <main className="auth-card st-card" aria-busy="true">
          <p className="subtitle">Opening your upload…</p>
        </main>
      ) : step === 'upload' ? (
        <main className="auth-card st-card">
          <h1>
            Check your bill <span className="italic">before you pay it.</span>
          </h1>
          <p className="subtitle">
            Free first look, sent to your email.
          </p>

          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              void addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <input
            ref={pickRef}
            type="file"
            accept={ACCEPT}
            multiple
            hidden
            onChange={(e) => {
              void addFiles(e.target.files);
              e.target.value = '';
            }}
          />

          {files.length === 0 ? (
            hasCamera ? (
              <div className="st-pick">
                <button type="button" className="st-pick-btn st-camera" onClick={() => cameraRef.current?.click()}>
                  <Camera size={18} aria-hidden="true" />
                  Take a photo of your bill
                </button>
                <button type="button" className="st-pick-btn" onClick={() => pickRef.current?.click()}>
                  <FileText size={18} aria-hidden="true" />
                  Upload a PDF or photo
                </button>
              </div>
            ) : (
              <div
                className={`st-drop${dragging ? ' is-over' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  void addFiles(e.dataTransfer.files);
                }}
              >
                <span className="st-drop-icon" aria-hidden="true">
                  <Upload size={20} />
                </span>
                <p className="st-drop-title">Drag your bill here</p>
                <p className="st-drop-sub">PDF or photo, up to 10 MB</p>
                <button type="button" className="st-pick-btn st-camera st-drop-btn" onClick={() => pickRef.current?.click()}>
                  Choose a file
                </button>
              </div>
            )
          ) : (
            <>
              <ul className="st-files">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`}>
                    <FileText size={15} aria-hidden="true" />
                    <span>{i === 0 ? f.name : `Page ${i + 1}: ${f.name}`}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${f.name}`}
                      onClick={() => setFiles((now) => now.filter((_, j) => j !== i))}
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
              {files.length < MAX_FILES && (
                <button type="button" className="st-add" onClick={() => pickRef.current?.click()}>
                  <Plus size={15} aria-hidden="true" /> Add another page
                </button>
              )}

              <label className="st-due">
                <span>When is it due? (optional)</span>
                <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </label>
            </>
          )}

          {files.length === 0 && (
            <div className="st-nobill">
              {remindDone ? (
                <p className="st-nobill-done">
                  <Check size={15} aria-hidden="true" /> Done. Check your inbox for how to get your itemized bill. The link in
                  that email brings you straight back here.
                </p>
              ) : !remindOpen ? (
                <button
                  type="button"
                  className="st-nobill-toggle"
                  onClick={() => {
                    setRemindOpen(true);
                    setError(null);
                    phStartStep('remind_opened');
                  }}
                >
                  No bill with you? Get a reminder
                </button>
              ) : (
                <form
                  className="st-nobill-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void askForReminder();
                  }}
                >
                  <p className="st-nobill-title">No bill with you?</p>
                  <p className="st-nobill-sub">
                    We'll email you how to get your itemized bill, and a link back here when you have it.
                  </p>
                  <label>
                    <span>Email</span>
                    <input
                      type="email"
                      autoComplete="email"
                      required
                      value={remindEmail}
                      onChange={(e) => setRemindEmail(e.target.value)}
                      placeholder="you@example.com"
                    />
                  </label>
                  <label>
                    <span>Hospital or provider (optional)</span>
                    <input
                      type="text"
                      value={remindProvider}
                      onChange={(e) => setRemindProvider(e.target.value)}
                      placeholder="e.g. Mercy Hospital"
                      maxLength={120}
                    />
                  </label>
                  <label>
                    <span>When is it due? (optional)</span>
                    <input type="date" value={remindDue} onChange={(e) => setRemindDue(e.target.value)} />
                  </label>
                  <button type="submit" className="primary" disabled={busy}>
                    {busy ? 'Sending…' : 'Remind me'}
                  </button>
                  <p className="st-nobill-fine">Three emails at most, and you can stop them any time.</p>
                </form>
              )}
            </div>
          )}

          {error && <p className="error">{error}</p>}

          {files.length > 0 && (
            <button type="button" className="primary" onClick={() => void upload()} disabled={busy}>
              {busy ? 'Uploading…' : 'Check my bill'}
            </button>
          )}

          <div className="st-fine">
            <p>
              <Lock size={13} aria-hidden="true" /> Private and encrypted
            </p>
            <p>
              By uploading you agree to our{' '}
              <a href="https://mediloop.ai/terms-of-service" target="_blank" rel="noreferrer">Terms</a> and{' '}
              <a href="https://mediloop.ai/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.
            </p>
          </div>

          {/* The fast lane (2026-10-16): someone who already knows they want
              us on it can pay first and upload after, same as before free
              accounts. */}
          <p className="st-fastlane">
            Want a specialist on it right away?{' '}
            <a href="https://mediloop.ai/checkout?plan=one_off" onClick={() => phStartStep('pay_now_clicked')}>
              Start your case for $129
            </a>
          </p>

          <p className="login-link">
            Already have an account?{' '}
            <Link to="/login" className="login-link-action">
              Log in
            </Link>
          </p>
        </main>
      ) : (
        <main className="auth-card su-card st-card">
          <p className="st-done">
            <Check size={15} strokeWidth={3} aria-hidden="true" /> Your bill is uploaded
          </p>
          <h1>
            Where should we send <span className="italic">your first look?</span>
          </h1>
          <p className="subtitle">We'll email you a code to open it. No password, and it's free.</p>

          <div className="st-phone">
            <label htmlFor="st-phone">Phone number</label>
            <input
              id="st-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(555) 123-4567"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setPhoneError(null);
              }}
            />
            {phoneError ? (
              <p className="error">{phoneError}</p>
            ) : (
              <p className="st-phone-fine">In case we need to reach you about your bill. We may call or text.</p>
            )}
          </div>

          <button type="button" className="su-google" onClick={() => void google()}>
            <img src={googleLogo} alt="" />
            Continue with Google
          </button>

          <div className="su-or">
            <span />
            <p>or</p>
            <span />
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void sendCode();
            }}
          >
            <input
              type="text"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              maxLength={40}
            />
            <input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            {error && <p className="error">{error}</p>}
            <button type="submit" className="primary" disabled={busy}>
              {busy ? 'Sending code…' : 'Email me the code'}
            </button>
          </form>

          <AuthHelp page="signup" email={email} label="Stuck?" />

          <p className="terms">
            By continuing, you agree to our{' '}
            <a href="https://mediloop.ai/terms-of-service" target="_blank" rel="noreferrer">
              Terms
            </a>{' '}
            and{' '}
            <a href="https://mediloop.ai/privacy-policy" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
            .
          </p>
        </main>
      )}
    </div>
  );
}
